const { axios } = require('@nuskin/axios-util')
const NodeCache = require("node-cache");
const cache = new NodeCache({stdTTL: 5 * 60, maxKeys: 100});

const baseUrl = 'https://graphql.contentstack.com/stacks'

const queryServiceLevel = `{
  marketConnection {
    edges {
      node {
        ... on Market {
          iso_code_2
        }
      }
    }
  }
  service_level {
    default
    order_price_range {
      above
      shipping_cost
    }
    service_levelConnection {
      edges {
        node {
          ... on ShippingServiceLevel {
            title
            description
            code
          }
        }
      }
    }
    shipping_cost
    zone_override {
      order_price_range {
        above
        shipping_cost
      }
      region_code
      shipping_cost
    }
  }
  system {
    uid
  }
  title
}`

const queryAllServiceLevel = `{
  all_shipping_service_levels_by_market(
    locale: $locale
  ) {
    items ${queryServiceLevel}
  }
}`

const queryMarketServiceLevel = `{
  all_shipping_service_levels_by_market(
    locale: $locale
    where: {market: {market: {iso_code_2: $market}}}
  ) {
    items ${queryServiceLevel}
  }
}`

/**
 * Shipping Options initialization and Settings
 * settings {
 *  apiKey - the ContentStack Stack API Key
 *  accessToken - the ContentStack Delivery Token
 *  env - the ContentStack environment
 *  locale - the ContentStack localization for localized content
 * }
 */
module.exports.shippingOptions = (settings) => {
  return {

    /**
     * return all shipping service levels
     * @returns {Object} shipping service levels
     */
    getShippingServiceLevels: async () => {
      const response = await __getAllShippingLevels()
      const items = response.data.all_shipping_service_levels_by_market 
        ? response.data.all_shipping_service_levels_by_market.items : []
      for (const i of items) {
        __fixItems(i)
      }
      return items
    },

    /**
     * return shipping service levels for a market
     * @param {String} market
     * @returns {Object} shipping service levels
     */
    getMarketShippingServiceLevels: getMarketShippingServiceLevels = async (market) => {
      const response = await __getMarketShippingLevels(market)
      const items = response.data.all_shipping_service_levels_by_market 
        ? response.data.all_shipping_service_levels_by_market.items[0] : undefined
      if (items) {
        __fixItems(items)
      }
      return items
    },

    /**
     * return shipping service levels for a region within a market
     * @param {String} market
     * @param {String} region
     * @returns {Object} shipping service levels
     */
    getRegionShippingServiceLevels: getRegionShippingServiceLevels = async (market, region) => {
      const levels =  await getMarketShippingServiceLevels(market)
      /* istanbul ignore else path not taken */
      if (levels) {
        for (const key of Object.keys(levels.service_level)) {
          const sl = levels.service_level[key]
          for (const zo of sl.zone_override) {
          /* istanbul ignore else path not taken */
            if (zo.region_code.includes(region)) {
              Object.assign(sl, zo)
            }
          }
          delete sl.zone_override
        }
      }
      return levels
    },

    /**
     * return shipping service levels cost for a region within a market and by order price
     * @param {String} market
     * @param {String} region
     * @param {Number} orderPrice
     * @returns {Object} shipping service levels
     */
    getMarketShippingServiceLevelsCost: async (market, region, orderPrice) => {
      const levels =  await getRegionShippingServiceLevels(market, region)
      /* istanbul ignore else path not taken */
      if (levels) {
        for (const key of Object.keys(levels.service_level)) {
          const sl = levels.service_level[key]
          let savedRange = 0
          for (const range of (sl.order_price_range || [])) {
          /* istanbul ignore else path not taken */
            if (orderPrice > range.above && range.above > savedRange) {
              sl.shipping_cost = range.shipping_cost
              savedRange = range.above
            }
          }
          delete sl.order_price_range
        }
      }
      return levels
    }
  }

  /**
   * Call to ContentStack to get shipping service levels for all markets
   * @returns {Object} shipping service levels
   */
  async function __getAllShippingLevels() {
    let response = cache.get(`all ${settings.locale}`)
    /* istanbul ignore if path not taken */
    if (response) {
      return response
    }

    const request = {
      url: `${baseUrl}/${settings.apiKey}`,
      method: 'post',
      headers: {access_token: settings.accessToken},
      params: {environment: settings.env},
      data: { 
        query: queryAllServiceLevel.replace('$locale',`"${settings.locale}"`)
      }
    }

    response = await axios(request)
    cache.set(`all ${settings.locale}`, response.data)
    return response.data
  }

  /**
   * Call to ContentStack to get shipping service levels for a specific market
   * @param {String} market 
   * @returns {Object} shipping service levels
   */
  async function __getMarketShippingLevels(market) {
    let response = cache.get(`${market} ${settings.locale}`)
    if (response) {
      return response
    }

    const request = {
      url: `${baseUrl}/${settings.apiKey}`,
      method: 'post',
      headers: {access_token: settings.accessToken},
      params: {environment: settings.env}, 
      data: { 
        query: queryMarketServiceLevel.replace('$locale',`"${settings.locale}"`).replace('$market',`"${market}"`), 
        variables: {market: market} //todo: I can't get variables to work so I am doing the replacement above
      }
    }
    response = await axios(request)
    cache.set(`${market} ${settings.locale}`, response.data)
    return response.data
  }
}

/**
 * Transform the ContentStack response into a more user friendly response
 * @param {*} items 
 */
function __fixItems(items) {
  items['uid'] = items.system.uid
  delete items.system

  items['market'] = items.marketConnection.edges[0].node.iso_code_2
  delete items.marketConnection

  // convert from array to map and cleanup service levels
  items.service_level = items.service_level.reduce((m, e) => {
    const type = e.service_levelConnection.edges[0].node
    Object.assign(type, e)
    m[type.code] = type
    delete type.service_levelConnection
    delete type.code
    return m
  }, {})
}
